import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { LayoutDashboard, Users, FileCheck2, DollarSign, ShieldAlert, BarChart, Check, X, FileText, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { Badge } from '@/components/ui/badge';
import Divider from '@/components/ui/divider';
import { adminSidebarLinks } from '@/config/adminSidebarLinks';

const mockValidations = [
  { id: 1, name: 'Alpha Barry', type: 'Nouveau transporteur', date: '2025-04-10', status: 'pending' },
  { id: 2, name: 'Moussa Camara', type: 'Vérification de véhicule', date: '2025-04-09', status: 'pending' },
  { id: 3, name: 'Kadiatou Sylla', type: 'Mise à jour d\'assurance', date: '2025-04-08', status: 'pending' },
  { id: 4, 'name': 'Transport Express', type: 'Nouveau transporteur', date: '2025-04-05', status: 'approved' },
];

const AdminValidations = () => {
  const [validations, setValidations] = useState([]);

  useEffect(() => {
    const storedValidations = JSON.parse(localStorage.getItem('admin_validations'));
    if (storedValidations) {
      setValidations(storedValidations);
    } else {
      setValidations(mockValidations);
      localStorage.setItem('admin_validations', JSON.stringify(mockValidations));
    }
  }, []);

  const handleAction = (id, newStatus) => {
    const updatedValidations = validations.map(v => v.id === id ? { ...v, status: newStatus } : v);
    setValidations(updatedValidations);
    localStorage.setItem('admin_validations', JSON.stringify(updatedValidations));
    toast({
      title: 'Action effectuée',
      description: `La demande a été ${newStatus === 'approved' ? 'approuvée' : 'rejetée'}.`,
    });
  };
  
  const pendingValidations = validations.filter(v => v.status === 'pending');
  const processedValidations = validations.filter(v => v.status !== 'pending');
  
  const getStatusBadge = (status) => {
    const statusMap = {
      approved: 'bg-green-500/20 text-green-400 border-green-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return <Badge className={`capitalize ${statusMap[status]}`}>{status === 'approved' ? 'Approuvé' : 'Rejeté'}</Badge>;
  };
  
  return (
    <>
      <Helmet><title>Validations en attente - LogiConnect</title></Helmet>
      <DashboardLayout sidebarLinks={adminSidebarLinks}>
        <h1 className="text-3xl font-bold text-white mb-8">Validations en attente</h1>

        <div className="bg-primary-700/50 border border-primary-600 rounded-xl">
          <div className="p-4 sm:p-6 border-b border-primary-600">
            <h2 className="text-xl font-semibold text-white">Demandes à traiter ({pendingValidations.length})</h2>
          </div>
          <div className="p-4 sm:p-6 space-y-4">
            {pendingValidations.length > 0 ? pendingValidations.map((item) => (
              <div key={item.id} className="bg-primary-900/70 p-4 rounded-lg flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:bg-primary-900 transition-colors">
                <div className="flex-1">
                  <p className="font-semibold text-lg text-white">{item.name}</p>
                  <div className="text-sm text-primary-300 flex flex-col sm:flex-row sm:items-center gap-x-4 gap-y-1 mt-1">
                    <span>Type: {item.type}</span>
                    <span>Date: {item.date}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 self-end md:self-center">
                  <Button variant="outline" className="border-secondary text-secondary hover:bg-secondary hover:text-primary">
                    <FileText className="w-4 h-4 mr-2" /> Docs
                  </Button>
                  <Button onClick={() => handleAction(item.id, 'approved')} className="bg-green-600 hover:bg-green-700 text-white">
                    <Check className="w-4 h-4 mr-2" /> Approuver
                  </Button>
                  <Button onClick={() => handleAction(item.id, 'rejected')} variant="destructive">
                    <X className="w-4 h-4 mr-2" /> Rejeter
                  </Button>
                </div>
              </div>
            )) : <p className="text-primary-300 text-center py-8">Aucune validation en attente.</p>}
          </div>
        </div>

        {/* Divider */}
        <Divider variant="gradient" icon={FileCheck2} label="Historique des Validations" />

        <div className="bg-primary-700/50 border border-primary-600 rounded-xl">
          <div className="p-4 sm:p-6 border-b border-primary-600">
            <h2 className="text-xl font-semibold text-white">Historique des validations</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-primary-300">
              <thead className="text-xs text-primary-300 uppercase bg-primary-900/50">
                <tr>
                  <th scope="col" className="px-6 py-3">Nom</th>
                  <th scope="col" className="px-6 py-3 hidden sm:table-cell">Type</th>
                  <th scope="col" className="px-6 py-3 hidden md:table-cell">Date</th>
                  <th scope="col" className="px-6 py-3">Statut</th>
                </tr>
              </thead>
              <tbody>
                {processedValidations.map(item => (
                  <tr key={item.id} className="border-b border-primary-700 hover:bg-primary-900/30">
                    <td className="px-6 py-4 font-medium text-white">{item.name}</td>
                    <td className="px-6 py-4 hidden sm:table-cell">{item.type}</td>
                    <td className="px-6 py-4 hidden md:table-cell">{item.date}</td>
                    <td className="px-6 py-4">{getStatusBadge(item.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </DashboardLayout>
    </>
  );
};

export default AdminValidations;
